import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const CURRENT_VERSION = '0.9.0';
const SEEN_KEY = `whats-new-seen-${CURRENT_VERSION}`;

const CHANGELOG = [
  {
    version: '0.9.0',
    date: 'May 2025',
    label: 'Latest',
    accent: '#6366f1',
    entries: [
      { icon: '🎤', title: 'Voice HUD', desc: 'Floating voice control bar with mute, deafen, camera & screen share. Timer shows how long you\'ve been connected.' },
      { icon: '📞', title: 'Incoming Call Toast', desc: 'Ring notification with accept/decline buttons and a 30-second countdown. Auto-declines if ignored.' },
      { icon: '🎵', title: 'Rich Presence Cards', desc: 'Activity panel now shows Spotify tracks with live progress bar, game details, and relative timestamps.' },
      { icon: '👤', title: 'User Hover Card', desc: 'Hover over any username in chat to see a quick profile card with status, role, and quick-action buttons.' },
      { icon: '🧵', title: 'Thread Reply Badges', desc: 'Messages with thread replies show a reply count badge and last-reply timestamp inline.' },
      { icon: '🔔', title: 'Server Unread Dots', desc: 'Server icons now show unread pips (white) and mention pips (red) in the left rail.' },
      { icon: '↕️', title: 'Drag-to-Reorder Servers', desc: 'Drag server icons up and down to reorder them. Order is saved locally.' },
    ],
  },
  {
    version: '0.8.0',
    date: 'Apr 2025',
    accent: '#06b6d4',
    entries: [
      { icon: '📊', title: 'Polls', desc: 'Create polls in any channel with multiple options, vote counts, and optional close dates.' },
      { icon: '🔖', title: 'Bookmarks', desc: 'Bookmark any message and access them from the sidebar quick-access bar.' },
      { icon: '📌', title: 'Pinned Messages', desc: 'Pin important messages to a channel — admins can pin, everyone can view.' },
      { icon: '🎭', title: 'Role Editor', desc: 'Full role management with permissions matrix, color picker, and member assignment.' },
      { icon: '🔍', title: 'Quick Switcher', desc: 'Ctrl+K to jump between channels and servers instantly.' },
    ],
  },
  {
    version: '0.7.0',
    date: 'Mar 2025',
    accent: '#22c55e',
    entries: [
      { icon: '🔗', title: 'URL Embeds', desc: 'Links in chat auto-expand with title, description, and preview image.' },
      { icon: '😊', title: 'Emoji Reactions', desc: 'React to messages with emoji. Hover to see who reacted.' },
      { icon: '↩️', title: 'Reply Threads', desc: 'Reply to any message inline or open a dedicated thread panel.' },
      { icon: '🖼️', title: 'Image Lightbox', desc: 'Click any image to view full-size with zoom controls.' },
    ],
  },
];

export function WhatsNewModal() {
  const { isOpen, type, onClose, onOpen } = useModalStore();
  const [tab, setTab] = useState(0);
  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'whatsNew');

  if (!isOpen || type !== 'whatsNew') return null;

  const release = CHANGELOG[tab];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="What's New"
        onClick={e => e.stopPropagation()}
        style={{ background: '#0f1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, width: 560, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.8)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '22px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 4 }}>✨ What's New</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>AeThex Connect — Release Notes</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4 }}>✕</button>
          </div>

          {/* Version tabs */}
          <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
            {CHANGELOG.map((r, i) => (
              <button
                key={r.version}
                onClick={() => setTab(i)}
                style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                  background: tab === i ? `${r.accent}22` : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${tab === i ? r.accent : 'rgba(255,255,255,0.08)'}`,
                  color: tab === i ? r.accent : 'rgba(255,255,255,0.45)',
                }}
              >
                v{r.version}
                {r.label && <span style={{ marginLeft: 5, fontSize: 9, background: r.accent, color: '#000', borderRadius: 8, padding: '1px 5px', fontWeight: 800 }}>{r.label}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Entry list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 24px' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontWeight: 600, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            {release.date} · v{release.version}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {release.entries.map((e, i) => (
              <div
                key={i}
                style={{ display: 'flex', gap: 14, padding: '13px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${release.accent}18`, border: `1px solid ${release.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                  {e.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 3 }}>{e.title}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>{e.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>AeThex Connect v{CURRENT_VERSION}</span>
          <button
            onClick={onClose}
            style={{ padding: '8px 22px', borderRadius: 8, background: '#6366f1', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Got it 🎉
          </button>
        </div>
      </div>
    </div>
  );
}

// Auto-show once per version on app load
export function useWhatsNewAutoShow() {
  const { onOpen } = useModalStore();
  useEffect(() => {
    const seen = localStorage.getItem(SEEN_KEY);
    if (!seen) {
      const t = setTimeout(() => {
        localStorage.setItem(SEEN_KEY, '1');
        onOpen('whatsNew');
      }, 2500);
      return () => clearTimeout(t);
    }
  }, []);
}
